import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';
import { InfoBox } from '../Components';
import { formatCents, formatEuros, CustomLegend } from './ChartUtils';

const SpotPriceComparison = ({ data }) => {
  const [activeData, setActiveData] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const chartRef = useRef(null);
  const processedData = useMemo(() => {
    if (!data?.spot_comparison) return [];
    
    return Object.entries(data.spot_comparison).map(([date, values]) => ({
      date,
      spot_avg: Number(values.spot_avg) || 0,
      realised_avg: Number(values.realised_avg) || 0,
      spot_max: Number(values.spot_max) || 0,
      estimated_saving: Number(values.estimated_saving) || 0
    }));
  }, [data]);

  const spotInfo = [
    { 
      label: "Toteutunut sähkön keskihinta", 
      value: formatCents(data.metadata.spot_comparison.realised_avg_mtd) 
    },
    { 
      label: "Pörssisähkön keskihinta", 
      value: formatCents(data.metadata.spot_comparison.spot_avg_mtd)
    },
    { 
      label: "Arvioitu säästö (pörssisähkö)", 
      value: formatEuros(data.metadata.spot_comparison.estimated_saving)
    },
    { 
      label: "Arvioitu kulutusvaikutus", 
      value: formatCents(data.metadata.spot_comparison.cost_factor_mtd) 
    }
  ];
  
  useEffect(() => {
    if (processedData.length > 0) {
      setActiveData(processedData[processedData.length - 1]);
    }
    
    const updateWidth = () => {
      if (chartRef.current) {
        const wrapper = chartRef.current.querySelector('.recharts-wrapper');
        if (wrapper) {
          setContainerWidth(wrapper.getBoundingClientRect().width);
        }
      }
    };
    
    // Initial update after a short delay to ensure chart is rendered
    const timer = setTimeout(updateWidth, 100);
    
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timer);
    };
  }, [processedData]);

  const handleMouseMove = (props) => {
    if (props.activePayload) {
      setActiveData(props.activePayload[0].payload);
    }
  };

  const handleMouseLeave = () => {
    setActiveData(processedData[processedData.length - 1]);
  };

  if (!processedData.length) return null;

  return (
    <div ref={chartRef} className="h-[400px]">
      <InfoBox title="Sähkön hintavertailu" dataPoints={spotInfo} />
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart 
          data={processedData}
          margin={{ top: 10, right: 30, left: 0, bottom: 30 }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="date" 
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis 
            yAxisId="left"
            label={{ value: 'Price', angle: -90, position: 'insideLeft' }}
          />
          <YAxis 
            yAxisId="right" 
            orientation="right"
            label={{ value: 'Savings', angle: 90, position: 'insideRight' }}
          />
          <Tooltip 
            formatter={(value, name) => {
              if (name === 'Arvioitu säästö') return formatEuros(value);
              else return formatCents(value);
            }}
          />
          <Line 
            yAxisId="left"
            type="monotone" 
            dataKey="spot_avg" 
            stroke="#8884d8" 
            name="Sähköpörssin keskihinta"
            dot={false}
          />
          <Line 
            yAxisId="left"
            type="monotone" 
            dataKey="realised_avg" 
            stroke="#82ca9d" 
            name="Toteutunut keskihinta"
            dot={false}
          />
          <Line 
            yAxisId="left"
            type="monotone" 
            dataKey="spot_max" 
            stroke="#ff7300" 
            name="Sähköpörssin korkein hinta"
            dot={false}
          />
          <Bar 
            yAxisId="right"
            dataKey="estimated_saving" 
            fill="#413ea0" 
            name="Arvioitu säästö"
            barSize={5}
          />
        </ComposedChart>
      </ResponsiveContainer>
      
      {containerWidth > 0 && (
        <div className="mt-4">
          <CustomLegend 
            payload={[
              { dataKey: 'spot_avg', name: 'Sähköpörssin keskihinta' },
              { dataKey: 'realised_avg', name: 'Toteutunut keskihinta' },
              { dataKey: 'spot_max', name: 'Sähköpörssin korkein hinta' },
              { dataKey: 'estimated_saving', name: 'Arvioitu säästö' }
            ]}
            colors={['#8884d8', '#82ca9d', '#ff7300', '#413ea0']}
            activeData={activeData}
            containerWidth={containerWidth}
            processedData={processedData}
            valueFormatter={(value, dataKey) => {
              if (dataKey === 'estimated_saving') return formatEuros(value);
              return formatCents(value);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SpotPriceComparison;
